import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faVenusMars,
  faStethoscope,
  faBriefcaseClock,
} from "@fortawesome/free-solid-svg-icons";
import DoctorSidebarCard from "../components/DoctorSidebarCard";
import InviteModal from "../components/InviteModal";
import "../styles/doctorProfile.css";

function DoctorProfile() {
  const { id } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        const res = await fetch(`https://directory.virtualtriage.ai/doctors/${id}`);
        const data = await res.json();
        setDoctor(data.data);
      } catch (error) {
        console.error("Error fetching doctor details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDoctor();
  }, [id]);

  if (loading) return <div>Loading...</div>;
  if (!doctor) return <div>No doctor found</div>;

  return (
    <div className="doctor-profile-page-wrapper">
      <Navbar />

      <div className="doctor-profile-content-wrapper" style={{ position: "relative" }}>
        {/* Blur overlay if doctor is unverified */}
        {!doctor.verified && <div className="blur-overlay" />}

        <div className={`main-doctor-content ${!doctor.verified ? "blurred" : ""}`}>
          <header className="profile-header">
            <div className="left-section">
              <div className="profile-picture">
                <img
                  src={doctor.image_url || "/assets/dummy-profile.png"}
                  alt="Doctor"
                  onError={(e) => (e.target.src = "/assets/dummy-profile.png")}
                />
              </div>
              <div className="profile-details">
                <h1>{doctor.full_name}</h1>
                <p>
                  <FontAwesomeIcon icon={faVenusMars} />
                  <strong> Gender:</strong> {doctor.gender}
                </p>
                <p>
                  <FontAwesomeIcon icon={faStethoscope} />
                  <strong> Specialties:</strong>{" "}
                  {Array.isArray(doctor.subspecialties)
                    ? doctor.subspecialties.join(", ")
                    : doctor.subspecialties}
                </p>
                <p>
                  <FontAwesomeIcon icon={faBriefcaseClock} />
                  <strong> Experience:</strong> {doctor.experience_years} years
                </p>
              </div>
            </div>
          </header>

          <div className="floating-sticky-wrapper">
            <div className="floating-sticky-card">
              <DoctorSidebarCard doctor={doctor} />
            </div>
          </div>

          <section className="doctor-section">
            <h2>About</h2>
            <p>{doctor.about}</p>
          </section>

          <section className="doctor-section">
            <h2>Qualifications</h2>
            <ul>
              {doctor.qualifications?.map((q, i) => (
                <li key={i}>{q}</li>
              ))}
            </ul>
          </section>

          <section className="doctor-section">
            <h2>Registration</h2>
            <p>
              <strong>Registered With:</strong> {doctor.registration?.body}
            </p>
            <p>
              <strong>Registration Number/ID:</strong>{" "}
              {doctor.registration?.number}
            </p>
          </section>

          <section className="doctor-section">
            <h2>Languages Spoken</h2>
            <ul>
              {doctor.languages_spoken?.map((lang, i) => (
                <li key={i}>{lang}</li>
              ))}
            </ul>
          </section>

          <section className="doctor-section">
            <h2>Insurers</h2>
            <ul>
              {doctor.insurers?.map((ins, i) => (
                <li key={i}>{ins}</li>
              ))}
            </ul>
          </section>

          <section className="doctor-section">
            <h2>Areas of Expertise</h2>
            <ul>
              {doctor.areas_of_expertise?.map((area, i) => (
                <li key={i}>{area}</li>
              ))}
            </ul>
          </section>

          <section className="doctor-section">
            <h2>Specialties</h2>
            <ul>
              {doctor.subspecialties?.map((sub, i) => (
                <li key={i}>{sub}</li>
              ))}
            </ul>
          </section>

          <section className="doctor-section">
            <h2>Conditions & Treatments</h2>
            <ul>
              {Array.isArray(doctor.conditions_and_treatments)
                ? doctor.conditions_and_treatments.map((cond, i) => (
                    <li key={i}>{cond}</li>
                  ))
                : doctor.conditions_and_treatments
                ? doctor.conditions_and_treatments
                    .split(",")
                    .map((cond, i) => <li key={i}>{cond.trim()}</li>)
                : [<li key="none">No conditions listed</li>]}
            </ul>
          </section>

          <section className="doctor-section">
            <h2>Diseases & Treatments</h2>
            <ul>
              {Array.isArray(doctor.diseases_and_treatments)
                ? doctor.diseases_and_treatments.map((cond, i) => (
                    <li key={i}>{cond}</li>
                  ))
                : doctor.diseases_and_treatments
                ? doctor.diseases_and_treatments
                    .split(",")
                    .map((cond, i) => <li key={i}>{cond.trim()}</li>)
                : [<li key="none">No conditions listed</li>]}
            </ul>
          </section>

          <section className="doctor-section-location-section">
            <h2>Location(s)</h2>
            {doctor.locations?.map((loc, i) => (
              <div key={i} className="location-card">
                <div className="location-map">
                  <iframe
                    width="100%"
                    height="200"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(
                      `${loc.address || ""}, ${loc.city || ""}, ${
                        loc.state || ""
                      }, ${loc.country || ""}, ${loc.postal_code || ""}`
                    )}&output=embed`}
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="location-details">
                  <p>{loc.address}</p>
                  <p>
                    {loc.city}, {loc.state}
                  </p>
                  <p>
                    {loc.country}, {loc.postal_code}
                  </p>
                </div>
              </div>
            ))}
          </section>

          <section className="doctor-section">
            <h2>Consultation Fees</h2>
            <p>
              <strong>New Appointment:</strong> £{doctor.new_appointment_fee}
            </p>
            <p>
              <strong>Follow-up:</strong> £{doctor.follow_up_fee}
            </p>
          </section>

          <section className="doctor-section">
            <h2>Skill Endorsements</h2>
            <ul>
              {doctor.skill_endorsements?.map((skill, i) => (
                <li key={i}>
                  {skill.skill} - Endorsed by{" "}
                  {skill.endorsed_by?.join(", ") || "None"} (
                  {typeof skill.count === "number" ? skill.count : 0} times)
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Invite Modal over blurred content */}
        {!doctor.verified && (
          <div className="invite-modal-overlay">
            <InviteModal
              isOpen={true}
              onClose={() => {}}
              doctorName={doctor.full_name}
              doctorEmail={doctor.email}
            />
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default DoctorProfile;
